import type { PanderrTemplateDefinition } from "../types";

export const LOCAL_SERVICE_TEMPLATE: PanderrTemplateDefinition = {
  id: "local-service-modern",
  name: "Local Service Modern",
  description:
    "A polished lead-generation website for contractors, home-service companies, and local service businesses.",
  category: "local-service",

  sections: [
    {
      id: "hero",
      type: "hero",
      eyebrow: "Trusted local service",
      heading: "Dependable service for your home, property, and peace of mind.",
      body: "Professional local service with clear communication, quality workmanship, and a straightforward experience from the first call to the final walkthrough.",

      images: [
        {
          src: "/studio/assets/stock/images/scott-blake-x-ghf9LjrVg-unsplash.jpg",
          alt: "Local service professional working on a residential property",
          role: "hero",
        },
      ],

      buttons: [
        {
          label: "Request a Free Quote",
          href: "/contact/",
          variant: "primary",
        },
        {
          label: "Explore Services",
          href: "/services/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "trust",
      type: "trust",
      eyebrow: "Local. Reliable. Professional.",
      heading: "The kind of service you feel comfortable recommending.",
      body: "Responsive communication, dependable scheduling, and attention to detail from a team that understands the value of doing the job right.",
    },

    {
      id: "services",
      type: "services",
      eyebrow: "How we can help",
      heading: "Practical solutions for the work your property needs.",
      body: "From everyday service calls to larger projects, we make it easy to understand your options and move forward with confidence.",

      services: [
        {
          title: "Repairs & Service",
          description:
            "Reliable help for everyday repairs, maintenance needs, and service calls.",
          image: {
            src: "/studio/assets/stock/images/josh-olalde-X1P1_EDNnok-unsplash.jpg",
            alt: "Professional completing hands-on service work",
            role: "service",
          },
          href: "/services/",
        },

        {
          title: "Installation & Projects",
          description:
            "Professional installation and project work completed with care, planning, and clear expectations.",
          image: {
            src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
            alt: "Contractor working on an installation project",
            role: "service",
          },
          href: "/services/",
        },

        {
          title: "Ongoing Maintenance",
          description:
            "Preventive maintenance and dependable ongoing support to help protect your property.",
          image: {
            src: "/studio/assets/stock/images/tim-mossholder-LpUJB0mEd-o-unsplash.jpg",
            alt: "Local service professional performing maintenance",
            role: "service",
          },
          href: "/services/",
        },
      ],
    },

    {
      id: "about",
      type: "about",
      eyebrow: "A better service experience",
      heading: "Good workmanship should come with good communication.",
      body: "You should know who is coming, what the work involves, and what to expect next. Our approach is built around dependable service, honest communication, and treating every property with respect.",

      images: [
        {
          src: "/studio/assets/stock/images/troy-allen-bLue0yFJD08-unsplash.jpg",
          alt: "Local service professional at a residential property",
          role: "about",
        },
      ],

      buttons: [
        {
          label: "Learn About Our Company",
          href: "/about/",
          variant: "text",
        },
      ],
    },

    {
      id: "process",
      type: "process",
      eyebrow: "Simple from start to finish",
      heading: "Getting the help you need shouldn't be complicated.",
      body: "Tell us what you need, receive clear next steps, and schedule the work when it makes sense for you.",
    },

    {
      id: "service-area",
      type: "service-area",
      eyebrow: "Proudly serving our community",
      heading: "Local service where you need it.",
      body: "We serve homeowners, property managers, and businesses throughout the surrounding area. Contact us to confirm service availability for your location.",

      buttons: [
        {
          label: "Check Your Service Area",
          href: "/contact/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "cta",
      type: "cta",
      eyebrow: "Ready to get started?",
      heading: "Tell us what your property needs.",
      body: "Request a quote and we'll help you understand the best next step for your project, repair, or service need.",

      buttons: [
        {
          label: "Request a Free Quote",
          href: "/contact/",
          variant: "primary",
        },
      ],
    },
  ],

  pages: [
    {
      id: "about",
      name: "About",
      title: "About Us",
      slug: "/about/",
      sections: [
        {
          id: "about-hero",
          type: "hero",
          eyebrow: "About our company",
          heading: "Local service built around trust, quality, and respect.",
          body: "We help homeowners, property managers, and businesses take care of their properties with dependable workmanship and straightforward communication.",
          images: [
            {
              src: "/studio/assets/stock/images/troy-allen-bLue0yFJD08-unsplash.jpg",
              alt: "Local service professional at a residential property",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Request a Quote",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "about-story",
          type: "about",
          eyebrow: "Who we are",
          heading: "Professional service without the runaround.",
          body: "Our goal is simple: make it easy to understand the work, know what to expect, and feel confident that your property is being treated with care.",
          images: [
            {
              src: "/studio/assets/stock/images/scott-blake-x-ghf9LjrVg-unsplash.jpg",
              alt: "Service professional working on a property",
              role: "about",
            },
          ],
        },

        {
          id: "about-trust",
          type: "trust",
          eyebrow: "What matters to us",
          heading: "Clear communication. Dependable service. Quality work.",
          body: "We believe the details matter—from showing up when promised to keeping you informed throughout the project.",
        },

        {
          id: "about-cta",
          type: "cta",
          eyebrow: "Let's talk",
          heading: "Need help with your property?",
          body: "Tell us what you need and we'll help you figure out the best next step.",
          buttons: [
            {
              label: "Contact Us",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "services",
      name: "Services",
      title: "Our Services",
      slug: "/services/",
      sections: [
        {
          id: "services-hero",
          type: "hero",
          eyebrow: "Our services",
          heading: "Reliable solutions for the work your property needs.",
          body: "From repairs and installations to ongoing maintenance, we provide practical service with clear expectations and dependable follow-through.",
          images: [
            {
              src: "/studio/assets/stock/images/josh-olalde-X1P1_EDNnok-unsplash.jpg",
              alt: "Professional completing service work",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Request a Quote",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "services-list",
          type: "services",
          eyebrow: "What we do",
          heading: "Service built around your needs.",
          body: "Choose the service that best fits your project, repair, or maintenance needs.",
          services: [
            {
              title: "Repairs & Service",
              description:
                "Dependable help for repairs, troubleshooting, and everyday service needs.",
              image: {
                src: "/studio/assets/stock/images/josh-olalde-X1P1_EDNnok-unsplash.jpg",
                alt: "Professional performing repair work",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Installation & Projects",
              description:
                "Professional installation and project work completed with planning, care, and attention to detail.",
              image: {
                src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
                alt: "Contractor working on an installation project",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Ongoing Maintenance",
              description:
                "Preventive maintenance and recurring service that helps protect your property.",
              image: {
                src: "/studio/assets/stock/images/tim-mossholder-LpUJB0mEd-o-unsplash.jpg",
                alt: "Professional performing maintenance",
                role: "service",
              },
              href: "/contact/",
            },
          ],
        },

        {
          id: "services-process",
          type: "process",
          eyebrow: "How it works",
          heading: "Simple from the first call to the finished job.",
          body: "Tell us what you need, receive clear next steps, schedule the work, and stay informed from start to finish.",
        },

        {
          id: "services-cta",
          type: "cta",
          eyebrow: "Ready to schedule?",
          heading: "Let's get your project moving.",
          body: "Reach out today and we'll help you determine the right service for your property.",
          buttons: [
            {
              label: "Request a Free Quote",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "our-work",
      name: "Our Work",
      title: "Our Work",
      slug: "/our-work/",
      sections: [
        {
          id: "work-hero",
          type: "hero",
          eyebrow: "Our work",
          heading: "See the care and attention we bring to every project.",
          body: "Browse examples of completed work, recent projects, and the kinds of improvements we help customers make.",
          images: [
            {
              src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
              alt: "Completed local service project",
              role: "hero",
            },
          ],
        },

        {
          id: "work-projects",
          type: "projects",
          eyebrow: "Recent projects",
          heading: "Work we're proud to put our name on.",
          body: "Use these project cards to highlight completed jobs, before-and-after results, or featured work.",
          projects: [
            {
              title: "Residential Project",
              description:
                "A completed residential service project with careful planning and professional execution.",
              image: {
                src: "/studio/assets/stock/images/scott-blake-x-ghf9LjrVg-unsplash.jpg",
                alt: "Residential service project",
                role: "project",
              },
            },
            {
              title: "Installation Project",
              description:
                "A professional installation completed with attention to detail and clear communication.",
              image: {
                src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
                alt: "Installation project",
                role: "project",
              },
            },
            {
              title: "Maintenance Work",
              description:
                "Ongoing service and maintenance designed to keep the property in good condition.",
              image: {
                src: "/studio/assets/stock/images/tim-mossholder-LpUJB0mEd-o-unsplash.jpg",
                alt: "Property maintenance project",
                role: "project",
              },
            },
          ],
        },

        {
          id: "work-cta",
          type: "cta",
          eyebrow: "Have a project in mind?",
          heading: "Let's talk about what you need.",
          body: "Request a quote and tell us about the work you're considering.",
          buttons: [
            {
              label: "Request a Quote",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact Us",
      slug: "/contact/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Get in touch",
          heading: "Tell us how we can help.",
          body: "Have a question, need service, or want an estimate? Reach out and we'll help you figure out the next step.",
          images: [
            {
              src: "/studio/assets/stock/images/troy-allen-bLue0yFJD08-unsplash.jpg",
              alt: "Local service professional ready to help",
              role: "hero",
            },
          ],
        },

        {
          id: "contact-details",
          type: "contact",
          eyebrow: "Contact information",
          heading: "We're here when you need us.",
          body: "Call, email, or use the contact form to tell us what you need. Add your business hours, service area, and contact details in Studio.",
          buttons: [
            {
              label: "Call Us",
              href: "tel:+15555555555",
              hrefField: "site.contact.phone",
              variant: "primary",
            },
            {
              label: "Email Us",
              href: "mailto:hello@example.com",
              hrefField: "site.contact.email",
              variant: "secondary",
            },
          ],
        },

        {
          id: "contact-area",
          type: "service-area",
          eyebrow: "Service area",
          heading: "Proudly serving our local community.",
          body: "Add the cities, neighborhoods, counties, or regions your business serves here.",
        },
      ],
    },
  ],
};
